// Чистите помощници на листа „Доставка", извадени от оркестратора: payload-ът
// на записа, текстът на грешката от BE, редовете на „Целия списък" и резюмето
// на черновата в промпта.
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import type {
  CreateDeliveryLine,
  CreateDeliveryPayload,
  DeliveryDocumentType,
  LastPrice,
  Packaging,
  Product,
  StockBalance,
} from "../../../../api/inventory";
import { withCurrentTime } from "../../components/dateTime";
import { roundMoney } from "../../components/numbers";
import type { CatalogItem } from "../../components/search/CatalogPickerModal";
import { catalogStockTone, isBelowMinimum } from "../../components/search/catalogStock";
import { pickableProducts } from "../../components/product/deletedProducts";
import { shownStock, soldWithoutStockOf } from "../../components/negativeStock";
import type { DeliverySaveValues } from "./DeliverySaveModal";
import {
  formatQtyInput,
  formatUnitPrice,
  isRowTouched,
  parseDecimal,
  type DeliveryDraft,
  type DeliveryRowDraft,
} from "./deliveryWorksheetShared";

type PayloadParams = {
  entries: [string, DeliveryRowDraft][];
  qtyBase: (row: DeliveryRowDraft) => number;
  values: DeliverySaveValues;
  warehouseId: number;
  supplierId: number | null;
  note: string;
  documentType: DeliveryDocumentType;
  linkedNoteIds: number[];
  // плащането се подава само при нова доставка
  withPayment: boolean;
};

export const buildDeliveryPayload = ({
  entries,
  qtyBase,
  values,
  warehouseId,
  supplierId,
  note,
  documentType,
  linkedNoteIds,
  withPayment,
}: PayloadParams): CreateDeliveryPayload => {
  // „Пази въведената сума": редът носи сумите от листа — бекендът записва
  // `netSum` ТОЧНО (документ, плащане, „Общо") и извежда ед. цената от нея.
  // Несметната още сума (празно поле) не се подава — тогава е к-во × цена.
  const lines: CreateDeliveryLine[] = entries.map(([pidStr, row]) => ({
    productId: +pidStr,
    warehouseId: row.warehouseId as number,
    packagingId: row.packagingId,
    packagingsCount: parseDecimal(row.qty),
    quantity: qtyBase(row),
    unitPrice: parseDecimal(row.price),
    ...(row.net !== "" ? { netSum: roundMoney(parseDecimal(row.net)) } : {}),
    ...(row.gross !== "" ? { grossSum: roundMoney(parseDecimal(row.gross)) } : {}),
    batchNumber: row.batch.trim() || null,
    expiryDate: row.expiry ? dayjs(row.expiry).toISOString() : null,
  }));
  return {
    warehouseId,
    date: (withCurrentTime(dayjs(values.date)) ?? dayjs()).toISOString(),
    invoiceNumber: values.documentNumber.trim() || null,
    supplierId,
    note: note.trim() || null,
    lines,
    documentType,
    ...(documentType === "invoice" ? { linkedNoteIds } : {}),
    ...(withPayment && values.paid
      ? { payment: { paidAt: dayjs().toISOString(), method: values.method, reference: null } }
      : {}),
  };
};

// Грешката от BE за оператора: detail (текст или списък от pydantic), иначе общото
export const deliveryErrorText = (error: unknown, t: TFunction) => {
  const err = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail))
    return detail
      .map((d) => (d as { msg?: string })?.msg)
      .filter(Boolean)
      .join("; ");
  return err.message || t("inventoryPage.errCreate");
};

// Въвеждането в реда на „Целия списък" (виж `catalogItemsOf`): редовете на
// листа, разфасовките и избран ли е доставчик
export type DeliveryCatalogContext = {
  rowOf: (productId: number) => DeliveryRowDraft;
  packagingById: Map<number, Packaging>;
  // с избран доставчик последните цени са само негови → чипът „От този доставчик"
  supplierSelected: boolean;
};

// Попълнен ли е редът в листа (тогава въведеното в списъка го презаписва
// само след въпрос) и какво се показва за него: количеството, иначе
// въведената сума
const filledValueOf = (row: DeliveryRowDraft): string | undefined => {
  if (row.qty !== "") return row.qty;
  const typedSum = row.typedSum === "net" ? row.net : row.typedSum === "gross" ? row.gross : "";
  return typedSum !== "" ? `${typedSum} €` : undefined;
};

// Полетата на реда: количеството е в мярката, в която листът го приема — нов
// ред е в базовата мярка, вече добавен — в разфасовката си (ако има);
// цената е нето за БАЗОВА единица: на реда, иначе последната доставна.
const entryFieldsOf = (
  product: Product,
  lastPrice: LastPrice | undefined,
  inSheet: boolean,
  context: DeliveryCatalogContext,
): Partial<CatalogItem> => {
  const lastPriceText = lastPrice ? formatUnitPrice(lastPrice.unitPrice) : undefined;
  if (!inSheet) return { entryUnit: product.unit ?? undefined, suggestedPrice: lastPriceText };
  const row = context.rowOf(product.id);
  const packaging = row.packagingId ? context.packagingById.get(row.packagingId) : undefined;
  return {
    entryUnit: (packaging ? packaging.name : product.unit) ?? undefined,
    entryFactor: packaging?.quantity ?? 1,
    suggestedPrice: row.price !== "" ? row.price : lastPriceText,
    filledValue: filledValueOf(row),
  };
};

// „Целият списък": каталогът в колони — единица · наличност (цвят по
// минимума; под нулата е 0, а минусът от продажби — приглушено до името
// „−10 продадени без наличност") · мин. количество · последна цена от
// доставчика/склада. Без изтритите продукти. С `context` — и полетата за
// въвеждане в реда, чиповете „Под минимума" и „От този доставчик".
export const catalogItemsOf = (
  products: Product[],
  balanceByProduct: Map<number, StockBalance>,
  lastPriceByProduct: Map<number, LastPrice>,
  added: Set<number>,
  decimalsOf: (productId: number) => number,
  t: TFunction,
  context?: DeliveryCatalogContext,
): CatalogItem[] =>
  pickableProducts(products).map((product) => {
    const balance = balanceByProduct.get(product.id);
    const lastPrice = lastPriceByProduct.get(product.id);
    const quantity = shownStock(balance?.quantity ?? 0);
    const soldWithoutStock = soldWithoutStockOf(balance?.quantity ?? 0);
    const minimum = product.minQuantity != null && product.minQuantity > 0 ? product.minQuantity : null;
    return {
      ...(context ? entryFieldsOf(product, lastPrice, added.has(product.id), context) : {}),
      ...(context && minimum != null
        ? {
            minimum: formatQtyInput(minimum, decimalsOf(product.id)),
            belowMinimum: isBelowMinimum(quantity, minimum),
          }
        : {}),
      ...(context?.supplierSelected && lastPrice ? { fromSupplier: true } : {}),
      id: product.id,
      name: product.name,
      group: product.category ?? null,
      unit: product.unit ?? undefined,
      stock: formatQtyInput(quantity, decimalsOf(product.id)),
      stockTone: catalogStockTone(quantity, product.minQuantity),
      price: lastPrice ? formatUnitPrice(lastPrice.unitPrice) : undefined,
      meta: [product.unit, lastPrice ? `${t("inventoryPage.wsLastPrice")} ${formatUnitPrice(lastPrice.unitPrice)}` : null]
        .filter(Boolean)
        .join(" · "),
      added: added.has(product.id),
      ...(soldWithoutStock
        ? {
            sub: t("inventoryPage.wsSoldWithoutStock", {
              qty: formatQtyInput(soldWithoutStock, decimalsOf(product.id)),
            }),
          }
        : {}),
    };
  });

// До 5 реда „Продукт — количество мярка" от черновата (промптът). Количеството е
// с толкова знака, колкото има мерната единица, и с мярката на реда.
export const draftSummaryOf = (
  draft: DeliveryDraft | null,
  productById: Map<number, Product>,
  packagingById: Map<number, Packaging>,
  decimalsOf: (productId: number) => number,
) =>
  draft
    ? Object.entries(draft.rows)
        .filter(([, row]) => isRowTouched(row))
        .slice(0, 5)
        .map(([pidStr, row]) => {
          const productId = +pidStr;
          const product = productById.get(productId);
          const unit = row.packagingId
            ? (packagingById.get(row.packagingId)?.name ?? "")
            : (product?.unit ?? "");
          const quantity = formatQtyInput(parseDecimal(row.qty), decimalsOf(productId));
          return `${product?.name ?? `#${pidStr}`} — ${quantity}${unit ? ` ${unit}` : ""}`;
        })
    : [];
